import * as React from 'react';
import { useIsMobile } from '@scramble/hooks';
import {
  FloatingArrow,
  FloatingFocusManager,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import type {
  TooltipOptions,
  TooltipTriggerProps,
  TriggerReferenceProps,
  TooltipContentProps,
} from './types';

const ARROW_HEIGHT = 7;
const GAP = 2;

type TooltipContextValue = ReturnType<typeof useTooltip>;

const useTooltip = ({
  initialOpen = false,
  placement = 'bottom',
  modal,
  delay,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  disabled = false,
}: TooltipOptions = {}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = React.useState<string | undefined>();
  const [descriptionId, setDescriptionId] = React.useState<string | undefined>();
  const arrowRef = React.useRef<SVGSVGElement | null>(null);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open: disabled ? false : open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      arrow({
        element: arrowRef,
        padding: 0,
      }),
      offset(ARROW_HEIGHT + GAP),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    enabled: !disabled && controlledOpen == null,
    delay: { close: delay },
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const interactions = useInteractions([hover, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      arrowRef,
      setOpen,
      disabled,
      ...interactions,
      ...data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    }),
    [
      open,
      setOpen,
      disabled,
      interactions,
      data,
      modal,
      labelId,
      descriptionId,
    ],
  );
};

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

type TooltipProps = React.PropsWithChildren<TooltipOptions>;

export function Tooltip({
  children,
  modal = false,
  ...restOptions
}: TooltipProps) {
  const popover = useTooltip({ modal, ...restOptions });

  return (
    <TooltipContext.Provider value={popover}>{children}</TooltipContext.Provider>
  );
}

export const TooltipTrigger = React.forwardRef<
  HTMLButtonElement,
  TooltipTriggerProps
>(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
  const context = useTooltipContext();
  const childRef =
    React.isValidElement(children) && 'ref' in children
      ? (children as React.ReactElement & { ref?: React.Ref<unknown> }).ref
      : null;
  const ref = useMergeRefs([context.refs.setReference, propRef, childRef]);

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...(children.props as Record<string, unknown>),
        'data-state': context.open ? 'open' : 'closed',
        'aria-disabled': context.disabled || undefined,
        ...(!context.disabled ? context.getReferenceProps() : {}),
      } as TriggerReferenceProps),
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      data-state={context.open ? 'open' : 'closed'}
      aria-disabled={context.disabled || undefined}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

export const TooltipContent = React.forwardRef<
  HTMLDivElement,
  TooltipContentProps
>(function TooltipContent({ style, arrowColor, ...props }, propRef) {
  const {
    context: floatingContext,
    arrowRef,
    disabled,
    ...context
  } = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);
  const isMobile = useIsMobile();

  if (disabled || !floatingContext.open) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager context={floatingContext} modal={context.modal}>
        <div
          ref={ref}
          style={{ ...context.floatingStyles, ...style }}
          aria-labelledby={context.labelId}
          aria-describedby={context.descriptionId}
          {...context.getFloatingProps(props)}
        >
          {props.children}
          {!isMobile && (
            <FloatingArrow
              ref={arrowRef}
              context={floatingContext}
              fill={arrowColor}
            />
          )}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
});
